import { useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { useDashboardMetrics } from "@/services/dashboardQueries";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { SessionTypeChart } from "@/components/dashboard/SessionTypeChart";
import { SessionTrendChart } from "@/components/dashboard/SessionTrendChart";
import { TopCompaniesTable } from "@/components/dashboard/TopCompaniesTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DateRangeSelector,
  type DateRange,
} from "@/components/dashboard/DateRangeSelector";
import {
  Activity,
  Users,
  Clock,
  TrendingUp,
  Star,
  Target,
  ThumbsUp,
  Wrench,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { QualityBar } from "@/components/analysis/QualityBar";

export function AdminDashboard() {
  const { localUser } = useAuthStore();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>(30);

  // Get dashboard metrics - filter by company if user is a company admin
  const companyId =
    localUser?.role?.name === "company_admin" && localUser.companyId
      ? localUser.companyId
      : undefined;
  const { data: metrics, isLoading } = useDashboardMetrics(
    companyId,
    dateRange
  );

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleCompanyClick = (companyId: string) => {
    navigate(`/admin/companies/${companyId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Welcome back, {localUser?.firstName} {localUser?.lastName}
          </p>
        </div>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Metric Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Sessions"
          value={metrics?.totalSessions.total.toLocaleString() ?? "0"}
          trend={metrics?.totalSessions.trend}
          icon={<Activity className="h-4 w-4" />}
          description="from last period"
          loading={isLoading}
        />
        <MetricCard
          title="Active Sessions"
          value={metrics?.activeSessions.toLocaleString() ?? "0"}
          icon={<Users className="h-4 w-4" />}
          description="currently active"
          loading={isLoading}
        />
        <MetricCard
          title="Avg Duration"
          value={metrics ? formatDuration(metrics.avgSessionDuration) : "0s"}
          icon={<Clock className="h-4 w-4" />}
          description="per session"
          loading={isLoading}
        />
        <MetricCard
          title="Session Growth"
          value={
            metrics?.totalSessions.trend !== undefined
              ? `${
                  metrics.totalSessions.trend > 0 ? "+" : ""
                }${metrics.totalSessions.trend.toFixed(1)}%`
              : "0%"
          }
          icon={<TrendingUp className="h-4 w-4" />}
          description="vs last period"
          loading={isLoading}
        />
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <SessionTrendChart
          data={metrics?.sessionTrend ?? []}
          loading={isLoading}
          dateRange={dateRange}
        />
        <SessionTypeChart
          data={
            metrics?.sessionsByType ?? {
              call: 0,
              web_voice: 0,
              web_chat: 0,
            }
          }
          loading={isLoading}
        />
      </div>

      {/* AI Analysis Metrics - Only show if we have analysis data */}
      {metrics?.analysisMetrics && (
        <>
          {/* Quality Score Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              title="Avg Quality Score"
              value={metrics.analysisMetrics.avgOverallScore.toFixed(1)}
              trend={metrics.analysisMetrics.qualityScoreTrend}
              icon={<Star className="h-4 w-4" />}
              description="out of 100"
              loading={isLoading}
            />
            <MetricCard
              title="Objective Achieved"
              value={`${metrics.analysisMetrics.objectiveAchievedRate}%`}
              icon={<Target className="h-4 w-4" />}
              description="of analyzed sessions"
              loading={isLoading}
            />
            <MetricCard
              title="Customer Satisfaction"
              value={`${metrics.analysisMetrics.customerSatisfactionRate}%`}
              icon={<ThumbsUp className="h-4 w-4" />}
              description="satisfied customers"
              loading={isLoading}
            />
            <MetricCard
              title="Tool Success Rate"
              value={`${metrics.analysisMetrics.technicalPerformance.toolSuccessRate}%`}
              icon={<Wrench className="h-4 w-4" />}
              description="accurate tool usage"
              loading={isLoading}
            />
          </div>

          {/* Conversation Quality & Technical Performance */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Conversation Quality Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5" />
                    <span>Conversation Quality</span>
                  </div>
                  <Badge variant="outline">
                    {metrics.analysisMetrics.analyzedSessionsCount} sessions
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <QualityBar
                    label="Response Appropriateness"
                    score={
                      metrics.analysisMetrics.avgConversationQuality
                        .responseAppropriateness
                    }
                  />
                  <QualityBar
                    label="Conversation Flow"
                    score={
                      metrics.analysisMetrics.avgConversationQuality
                        .conversationFlow
                    }
                  />
                  <QualityBar
                    label="Information Accuracy"
                    score={
                      metrics.analysisMetrics.avgConversationQuality
                        .informationAccuracy
                    }
                  />
                  <QualityBar
                    label="Clarity of Communication"
                    score={
                      metrics.analysisMetrics.avgConversationQuality
                        .clarityOfCommunication
                    }
                  />
                  <QualityBar
                    label="Professional Tone"
                    score={
                      metrics.analysisMetrics.avgConversationQuality
                        .professionalTone
                    }
                  />
                  <QualityBar
                    label="Empathy & Rapport"
                    score={
                      metrics.analysisMetrics.avgConversationQuality
                        .empathyAndRapport
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Technical & Efficiency Metrics */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Wrench className="h-5 w-5" />
                    <span>Technical Performance</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Tool Success Rate
                      </span>
                      <span className="font-medium text-green-600">
                        {
                          metrics.analysisMetrics.technicalPerformance
                            .toolSuccessRate
                        }
                        %
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Audio Issues
                      </span>
                      <span className="font-medium text-orange-600">
                        {
                          metrics.analysisMetrics.technicalPerformance
                            .audioIssuesPercent
                        }
                        %
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        System Errors
                      </span>
                      <span className="font-medium text-red-600">
                        {
                          metrics.analysisMetrics.technicalPerformance
                            .systemErrorsCount
                        }
                      </span>
                    </div>
                    {metrics.analysisMetrics.technicalPerformance
                      .topFunctionCalls.length > 0 && (
                      <div className="pt-2 border-t">
                        <span className="text-sm text-gray-600 block mb-2">
                          Top Function Calls
                        </span>
                        <div className="space-y-1">
                          {metrics.analysisMetrics.technicalPerformance.topFunctionCalls.map(
                            (fc, i) => (
                              <div
                                key={i}
                                className="flex justify-between items-center text-xs"
                              >
                                <Badge variant="outline" className="text-xs">
                                  {fc.name}
                                </Badge>
                                <span className="text-gray-500">
                                  {fc.count}x
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="h-5 w-5" />
                    <span>Efficiency Metrics</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Dead Air Detected
                      </span>
                      <span className="font-medium">
                        {
                          metrics.analysisMetrics.efficiencyMetrics
                            .deadAirPercent
                        }
                        %
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Unnecessary Repetition
                      </span>
                      <span className="font-medium">
                        {
                          metrics.analysisMetrics.efficiencyMetrics
                            .unnecessaryRepetitionPercent
                        }
                        %
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-sm text-gray-600">
                        Analysis Coverage
                      </span>
                      <span className="font-medium text-blue-600">
                        {Math.round(
                          (metrics.analysisMetrics.analyzedSessionsCount /
                            metrics.analysisMetrics.totalSessionsCount) *
                            100
                        )}
                        %
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 pt-1">
                      {metrics.analysisMetrics.analyzedSessionsCount} of{" "}
                      {metrics.analysisMetrics.totalSessionsCount} sessions
                      analyzed
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Analysis Not Available Message */}
      {!metrics?.analysisMetrics && !isLoading && (
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center text-center space-y-2">
              <AlertCircle className="h-12 w-12 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-700">
                No Analysis Data Available
              </h3>
              <p className="text-sm text-gray-500 max-w-md">
                Session analysis metrics will appear here once sessions have
                been analyzed. Analysis provides insights into conversation
                quality, technical performance, and efficiency.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Companies Table - Only show for super_admin and platform_admin */}
      {!companyId &&
        metrics?.topCompanies &&
        metrics.topCompanies.length > 0 && (
          <TopCompaniesTable
            companies={metrics.topCompanies}
            loading={isLoading}
            onCompanyClick={handleCompanyClick}
          />
        )}
    </div>
  );
}
